'use client';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Else, If, Then, When } from 'react-if';

import { Box } from '@nerdwallet/currency/components/Box';
import { Link } from '@nerdwallet/currency/components/Link';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { SecureEncryptionMessage } from '@nerdwallet/currency/components/SecureEncryptionMessage';
import { Title } from '@nerdwallet/currency/components/Title';
import { Type } from '@nerdwallet/currency/components/Type';

import { useMutation } from '@apollo/client';
import Paths from '@lib/paths';
import { useAnalytics } from '@lib/segment';
import { SegmentPageView } from '@lib/segment';
import {
  TuStep,
  useTuStepper,
} from '@components/transunion-flow/tu-modal-stepper';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import { ENROLL_CREDIT_PROFILE_MUTATION } from '../queries';
import ContinueButton from '../components/continue-button';
import {
  isAuthenticated,
  isBlocked,
  isDeceased,
  isTransunionDuplicate,
  isTransunionFailVerification,
  isTransunionNoData,
  isTransunionOtherThinFile,
  notEnoughData,
} from '@components/transunion-flow/utils';
import { TuCreditReportIdentityProfile } from '@generated-gql/generated-types';

const MESSAGES = {
  userIsDuplicate: 'You’ve already signed up for your credit score.',
  userHasTUDuplicateEmail: 'You’ve already signed up for your credit score.',
  userNoData: 'Looks like you’re just starting out.',
  userOtherFailure: 'Looks like you’re just starting out.',
  userFailedVerification: 'Hm. Looks like there was a typo.',
  userIsDeceased: 'The person associated with this SSN is deceased.',
  userNotEnoughData:
    'Uh-oh, looks like you don’t have enough credit history to display a credit report.',
  userEnrollFailed: 'Your identity couldn’t be verified. Try again in 30 days.',
};

interface Props {
  onActivationSuccess: (profile: TuCreditReportIdentityProfile) => void;
}

const BlockedStep = ({ onActivationSuccess }: Props) => {
  const [userEnrollFailed, setUserEnrollFailed] = useState<boolean>(false);
  const { segmentTrack } = useAnalytics();
  const { setTuVisibility } = useTuVisibilityContext();
  const { authStatusProfile, tuErrors } = useTuStepper();

  const [enrollTUCustomer, { loading }] = useMutation(
    ENROLL_CREDIT_PROFILE_MUTATION,
    {
      onCompleted: (data) => {
        // Successfully able to retrieve credit report
        onActivationSuccess(data.enrollCreditProfile);
      },
      onError: () => {
        // Enrollment failed, continue showing the blocked state
        setUserEnrollFailed(true);
      },
    },
  );

  useEffect(() => {
    // If a user is stuck in an authenticated state (i.e. passed initial verification, but
    // did not complete enrollment), attempt to enroll them again to get their credit report
    if (isAuthenticated(authStatusProfile)) {
      enrollTUCustomer();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const restartFlow = useCallback(() => {
    segmentTrack('TU Flow Link Account Clicked');

    setTuVisibility({
      show: true,
      tuStep: TuStep.TU_BASIC_INFO,
    });
  }, [segmentTrack, setTuVisibility]);

  const onForward = useCallback(() => {
    setTuVisibility({
      show: true,
      tuStep: TuStep.TU_END,
    });
  }, [setTuVisibility]);

  const {
    userIsBlocked,
    userNotEnoughData,
    userIsDeceased,
    userIsDuplicate,
    userIsRateLimited,
    userNoData,
    userFailedVerification,
    userOtherFailure,
    userHasTUDuplicateEmail,
    userTUDuplicateEmail,
  } = useMemo(() => {
    return {
      userIsBlocked: isBlocked(authStatusProfile),
      userNotEnoughData: notEnoughData(authStatusProfile),
      userIsDeceased: isDeceased(authStatusProfile),
      userIsDuplicate: isTransunionDuplicate(authStatusProfile),
      userIsRateLimited: tuErrors.isRateLimited,
      userNoData: isTransunionNoData(authStatusProfile),
      userFailedVerification: isTransunionFailVerification(authStatusProfile),
      userOtherFailure: isTransunionOtherThinFile(authStatusProfile),
      userHasTUDuplicateEmail: !!tuErrors.duplicateEmail,
      userTUDuplicateEmail: tuErrors.duplicateEmail,
    };
  }, [authStatusProfile, tuErrors]);

  const getTitle = () => {
    let message = null;
    switch (true) {
      case userIsDuplicate:
        message = MESSAGES.userIsDuplicate;
        break;
      case userHasTUDuplicateEmail:
        message = MESSAGES.userHasTUDuplicateEmail;
        break;
      case userNoData:
        message = MESSAGES.userNoData;
        break;
      case userOtherFailure:
        message = MESSAGES.userOtherFailure;
        break;
      case userFailedVerification:
        message = MESSAGES.userFailedVerification;
        break;
      case userIsDeceased:
        message = MESSAGES.userIsDeceased;
        break;
      case userNotEnoughData:
        message = MESSAGES.userNotEnoughData;
        break;
      case userEnrollFailed:
        message = MESSAGES.userEnrollFailed;
        break;
      default:
        message = 'Hm. We couldn’t get your credit score.';
    }

    return message;
  };

  const getBody = () => {
    // Special messages for different error statuses
    if (userIsDuplicate) {
      return (
        <Box>
          <Box>
            We know you&apos;re excited to get your credit score and report, but
            it looks like there&apos;s been too many attempts to get it.
          </Box>
          <Box marginTop="3-half">
            When that happens, TransUnion puts a temporary block for the next 30
            days — so give it another shot in about a month.
          </Box>
        </Box>
      );
    }
    if (userHasTUDuplicateEmail) {
      return (
        <Box>
          <Box>
            Based on what you&apos;ve told us, it looks like you already have an
            account with us.
          </Box>
          <Box marginTop="3-half">
            In case you forgot, here&apos;s the email you used (for security
            reasons, we&apos;ve hidden part of it):
          </Box>
          <Type bold marginTop="3-half">
            {userTUDuplicateEmail}
          </Type>
          <Box marginTop="3-half">
            Simply log in with this email to get your free credit score.
          </Box>
        </Box>
      );
    }
    if (userNoData || userOtherFailure) {
      return (
        <Box>
          <Box>
            We weren’t able to get your credit report because you don’t have
            enough credit history.
          </Box>
          <Box marginTop="3-half">
            But don’t worry, you’ll get there —{' '}
            <Link
              href="https://www.nerdwallet.com/blog/finance/how-to-build-credit/?trk_origin=mynerdwallet"
              target="_blank"
              rel="noopener noreferrer"
            >
              check out our tips on building credit
            </Link>
            .
          </Box>
        </Box>
      );
    }
    if (userFailedVerification) {
      return (
        <Box>
          <Box>
            We weren’t able to fetch your credit report, probably due to a small
            typo.
          </Box>
          <Box>
            Don’t worry, it happens to the best of us — let’s give it another
            shot.
          </Box>
        </Box>
      );
    }
    if (userIsDeceased) {
      return (
        <Box>
          To correct this error, please{' '}
          <Link
            href="https://www.transunion.com/credit-disputes/dispute-your-credit"
            target="_blank"
            rel="noopener noreferrer"
          >
            file a dispute
          </Link>{' '}
          with TransUnion.
        </Box>
      );
    }
    if (userNotEnoughData) {
      return (
        <Box>
          No worries. You can turn that around.{' '}
          <Link
            href="https://www.nerdwallet.com/blog/finance/how-to-build-credit/?trk_origin=mynerdwallet"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn how to build your credit.
          </Link>
        </Box>
      );
    }
    if (userEnrollFailed) {
      return (
        <Box>
          <Box>
            For security reasons, TransUnion places a block when they’re unable
            to verify your identity for 30 days.{' '}
          </Box>
          <Box marginTop="3-half">
            We recommend holding off until the 30 days have passed before trying
            to get your credit report.
          </Box>
        </Box>
      );
    }

    return 'You can attempt again in 30 days.';
  };

  const renderCTA = () => {
    if (userHasTUDuplicateEmail) {
      return (
        <ContinueButton
          onClick={() => {
            window.location.href = Paths.SIGNOUT;
          }}
          buttonText="Sign out"
        />
      );
    }

    if (
      userEnrollFailed ||
      userIsDuplicate ||
      userIsBlocked ||
      userIsRateLimited
    ) {
      return <ContinueButton buttonText="Ok, got it" onClick={onForward} />;
    }

    return <ContinueButton onClick={restartFlow} buttonText="Try again" />;
  };

  const title = getTitle();
  const body = getBody();
  const cta = renderCTA();

  return (
    <SegmentPageView name="credit_score_blocked">
      <Box display="flex" flexDirection="column" width={100}>
        <If condition={loading}>
          <Then>
            <Box display="flex" justifyContent="center" paddingY={4}>
              <LoadingSpinner />
            </Box>
          </Then>
          <Else>
            <Box>
              <When condition={!!title}>
                <Title size="small" bold>
                  {title}
                </Title>
              </When>
              <Box marginY={4}>
                <Type>{body}</Type>
              </Box>
              <When condition={!!cta}>
                <Box marginY={4}>{renderCTA()}</Box>
              </When>
            </Box>
            <Box marginTop={4}>
              <SecureEncryptionMessage />
            </Box>
          </Else>
        </If>
      </Box>
    </SegmentPageView>
  );
};

export default BlockedStep;
